import GenerativeCards from "../GenerativeCards";
import React from "react";
import { formatContent, isSafeUrl, safeUrl } from "./utils";
import { LinkIcon } from "./Icons";

const fmtTime = (ts) =>
  new Date(ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

const BOT_BUBBLE = "bg-white text-slate-800 border border-slate-100 rounded-tl-sm";
const USER_BUBBLE = "bg-indigo-600 text-white rounded-tr-sm";
const BUBBLE = "px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm break-words";

export function TypingBubble() {
  return (
    <div className="flex w-full mb-4 justify-start" data-testid="typing">
      <div className={`${BUBBLE} ${BOT_BUBBLE} flex gap-1.5 items-center`}>
        {[0, 1, 2].map((i) => (
          <span key={i} className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: `${i * 150}ms` }} />
        ))}
      </div>
    </div>
  );
}

export function MessageBubble({ msg, onOpenPreview }) {
  const isBot = msg.role === "bot";

  let body;
  if (msg.kind === "summary") {
    body = (
      <div className="w-full bg-white border border-slate-100 rounded-2xl p-4 shadow-sm text-[13px]">
        <h4 className="text-indigo-600 font-semibold mb-2">Your Summary</h4>
        {msg.rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-3 py-1">
            <span className="text-slate-500 font-semibold shrink-0">{label}</span>
            <span className="text-slate-800 text-right break-words">{value}</span>
          </div>
        ))}
      </div>
    );
  } else if (msg.kind === "invoice") {
    body = (
      <div className={`${BUBBLE} ${BOT_BUBBLE} flex flex-col gap-3`}>
        <div>Payment successful! Your booking is confirmed. Please download your invoice below.</div>
        <a href={safeUrl(msg.url)} target="_blank" rel="noopener noreferrer"
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg w-max text-sm font-medium no-underline">
          Download Invoice
        </a>
        <div className="text-[13px] text-slate-600">
          If you have any queries please contact support.<br />
          <b>Support:</b>{" "}
          <a href="tel:+917619264438" className="text-indigo-600 no-underline">+91 7619264438</a>
        </div>
      </div>
    );
  } else if (msg.kind === "ai") {
    body = (
      <>
        <div className={`${BUBBLE} ${BOT_BUBBLE}`}>
          <div dangerouslySetInnerHTML={{ __html: formatContent(msg.text) }} />

          {msg.toolsUsed?.length > 0 && (
            <div className="mt-2 pt-1.5 border-t border-slate-100 text-[11px] text-slate-400">
              🔍 Verified via live database ({msg.toolsUsed.join(", ")})
            </div>
          )}
          {msg.stamp && <span className="block text-[10px] mt-1.5 opacity-50">{fmtTime(msg.time)}</span>}
        </div>

        {/* Generative UI cards (tours, stays, cabs, sights) */}
        {msg.cards && (
          <div className="w-full min-w-0 mt-2">
            <GenerativeCards cards={msg.cards} onOpenPreview={onOpenPreview} />
          </div>
        )}

        {/* Fallback single-link card, only when there are no generative cards */}
        {!msg.cards && msg.preview?.url && isSafeUrl(msg.preview.url) && (
          <button type="button" onClick={() => onOpenPreview(msg.preview)}
            className="mt-2 w-full max-w-sm text-left group transition-all hover:-translate-y-0.5 active:translate-y-0">
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex items-start gap-3 hover:bg-indigo-100 hover:border-indigo-200 transition-colors shadow-sm">
              <div className="bg-indigo-200 p-2 rounded-lg text-indigo-700 shrink-0 mt-0.5"><LinkIcon /></div>
              <div className="min-w-0">
                <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider block">Live Package</span>
                <h4 className="text-sm font-semibold text-indigo-900 group-hover:text-indigo-700 truncate">
                  {msg.preview.title || "Tour Package"}
                </h4>
                <span className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wider mt-2 block">
                  Click to view in split view
                </span>
              </div>
            </div>
          </button>
        )}
      </>
    );
  } else {
    body = <div className={`${BUBBLE} ${isBot ? BOT_BUBBLE : USER_BUBBLE} whitespace-pre-wrap`}>{msg.text}</div>;
  }

  return (
    <div data-msg-id={msg.id} className={`flex w-full mb-4 ${isBot ? "justify-start" : "justify-end"}`}>
      <div className={`flex flex-col min-w-0 ${msg.cards ? "w-full max-w-full" : "max-w-[88%]"} ${msg.kind === "summary" ? "w-full" : ""} ${isBot ? "items-start" : "items-end"}`}>
        {body}
      </div>
    </div>
  );
}